/**
 * Hard caps for the bounded object-graph walk.
 *
 * These are deliberately compile-time constants rather than env-tunable: they
 * exist to guarantee the measurement can never become the memory problem it is
 * measuring, and that guarantee should not be defeatable from a task
 * definition. The one runtime control is the kill switch, which can only ever
 * make the walk cheaper (by skipping it entirely).
 *
 * `MAX_WALK_NODES` bounds both the node count and the traversal queue, so the
 * walk's own peak allocation is O(MAX_WALK_NODES) pointers — a couple of MB at
 * this size — regardless of how large the result actually is.
 *
 * It also bounds the walk's *time*, which is what makes the rate limiter's
 * guarantee meaningful: measured at 11-19 ms for a full 100k-node walk
 * (the spread is machine load), so the instrument's worst-case blocking CPU
 * is `walks/sec x ~19 ms` — under 4% of one core at the default 2 walks/sec —
 * and nothing about load or pod health can push it higher.
 */
export const MAX_WALK_NODES = 100_000;

/**
 * Prisma nests one object level per relation hop plus one array level, so a
 * five-hop include tree fits comfortably. Anything deeper is a pathological
 * shape we want reported as depth-capped rather than followed.
 */
export const MAX_WALK_DEPTH = 12;

/**
 * Distinct relation paths tracked for attribution. Paths come from the Prisma
 * schema so they are naturally bounded, but the cap keeps a surprising result
 * shape from growing an unbounded map. Overflow is bucketed under
 * {@link OVERFLOW_RELATION_PATH}.
 */
export const MAX_TRACKED_RELATION_PATHS = 256;

export const OVERFLOW_RELATION_PATH = "(other)";

/** How many relation paths are reported, largest contributor first. */
export const TOP_RELATION_COUNT = 5;
